import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import RenewalNotificationBell from './RenewalNotificationBell';
import ThemeToggle from '../UI/ThemeToggle';
import {
  HiOutlineHome,
  HiOutlineOfficeBuilding,
  HiOutlineClipboardList,
  HiOutlineUsers,
  HiOutlineChartBar,
  HiOutlineMenu,
  HiOutlineX,
  HiOutlineLogout,
  HiOutlineCog,
  HiOutlinePhone,
  HiOutlineSpeakerphone,
  HiOutlineTicket,
  HiOutlineCloudUpload,
  HiOutlineDocumentText,
  HiOutlineChatAlt2,
  HiOutlineChartPie,
  HiOutlineChartSquareBar,
  HiOutlineUserGroup,
  HiOutlineXCircle,
  HiOutlineCalendar,
  HiOutlineClock,
  HiOutlineReply,
  HiOutlineShieldCheck,
  HiOutlineExclamation,
  HiOutlineArrowCircleUp,
  HiOutlineLockOpen,
  HiOutlineCube,
  HiOutlineTrendingUp,
  HiOutlineClipboardCheck,
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlinePhotograph,
  HiOutlinePencilAlt,
  HiOutlineVideoCamera,
} from 'react-icons/hi';
import { canAccessSuperAdminPage } from '../../utils/superAdminAccess';

const navItems = [
  { path: '/superadmin/dashboard', label: 'Dashboard', icon: HiOutlineHome },
  { path: '/superadmin/organizations', label: 'Organizations', icon: HiOutlineOfficeBuilding },
  { path: '/superadmin/leads', label: 'Leads', icon: HiOutlinePhone },
  { path: '/superadmin/plans', label: 'Plans', icon: HiOutlineClipboardList },
  { path: '/superadmin/promocodes', label: 'Promo Codes', icon: HiOutlineTicket },
  { path: '/superadmin/channel-partners', label: 'Channel Partner', icon: HiOutlineUserGroup },
  { path: '/superadmin/demo-bookings', label: 'Demo Bookings', icon: HiOutlineCalendar },
  { path: '/superadmin/notices', label: 'Notices', icon: HiOutlineSpeakerphone },
  { path: '/superadmin/app-versions', label: 'App Versions', icon: HiOutlineCloudUpload },
  { path: '/superadmin/invoice-settings', label: 'Invoice Settings', icon: HiOutlineDocumentText },
  { path: '/superadmin/audit-logs', label: 'Audit Logs', icon: HiOutlineShieldCheck },
  { path: '/superadmin/settings', label: 'Settings', icon: HiOutlineCog },
];

// Sub-sections shown when the "Reports" button is clicked, grouped into
// Subscription (renewal/billing) reports and Support Tickets reports
const subscriptionChildren = [
  { path: '/superadmin/renewal-report', label: 'Renewals Overview', icon: HiOutlineChartBar },
  { path: '/superadmin/renewal-conversion', label: 'Renewal Success Rate', icon: HiOutlineChartPie },
  { path: '/superadmin/renewal-agent-performance', label: 'Renewal Team Performance', icon: HiOutlineUserGroup },
  { path: '/superadmin/renewal-loss', label: 'Lost Renewals', icon: HiOutlineXCircle },
  { path: '/superadmin/subscription-expiry', label: 'Subscription End Dates', icon: HiOutlineCalendar },
];

const ticketChildren = [
  { path: '/superadmin/sla-performance', label: 'On-Time Support (SLA)', icon: HiOutlineShieldCheck },
  { path: '/superadmin/first-response', label: 'First Reply Time', icon: HiOutlineReply },
  { path: '/superadmin/ticket-resolution', label: 'Time to Resolve', icon: HiOutlineClock },
  { path: '/superadmin/overdue-tickets', label: 'Overdue Tickets', icon: HiOutlineExclamation },
  { path: '/superadmin/escalations', label: 'Escalations', icon: HiOutlineArrowCircleUp },
  { path: '/superadmin/reopened-tickets', label: 'Reopened Tickets', icon: HiOutlineLockOpen },
  { path: '/superadmin/ticket-closure', label: 'Ticket Closure Rate', icon: HiOutlineClipboardCheck },
  { path: '/superadmin/ticket-trends', label: 'Ticket Trends', icon: HiOutlineTrendingUp },
  { path: '/superadmin/customer-tickets', label: 'Tickets by Customer', icon: HiOutlineUserGroup },
  { path: '/superadmin/product-tickets', label: 'Tickets by Product', icon: HiOutlineCube },
];

const allReportChildren = [...subscriptionChildren, ...ticketChildren];

// Sub-sections shown when the "Landing Page" button is clicked — content
// shown on the public landing page, grouped under one collapsible header.
const landingPageChildren = [
  { path: '/superadmin/blog', label: 'Blog', icon: HiOutlinePencilAlt },
  { path: '/superadmin/videos', label: 'Videos', icon: HiOutlineVideoCamera },
  { path: '/superadmin/other-products', label: 'Other Products', icon: HiOutlineCube },
];

const bottomNavItems = [
  { path: '/superadmin/support', label: 'Support', icon: HiOutlineChatAlt2 },
  { path: '/superadmin/partner-tickets', label: 'Partner Tickets', icon: HiOutlineTicket },
  { path: '/superadmin/admins', label: 'Admin Users', icon: HiOutlineUsers },
  // { path: '/superadmin/analytics', label: 'Analytics', icon: HiOutlineChartBar },
  // { path: '/superadmin/settings', label: 'Settings', icon: HiOutlineCog },
];

const SuperAdminSidebar = ({ isOpen, onClose }) => {
  const { superAdmin, logout, isSuperAdmin, hasPermission } = useSuperAdmin();
  const location = useLocation();
  const navigate = useNavigate();

  const canSee = (path) => canAccessSuperAdminPage(path, { isSuperAdmin, hasPermission });

  // Limited-access roles (support/billing) only see the nav items and report
  // sub-pages their permissions unlock — see utils/superAdminAccess.js
  const visibleNavItems = navItems.filter((item) => canSee(item.path));
  const visibleBottomNavItems = bottomNavItems.filter((item) => canSee(item.path));
  const visibleSubscriptionChildren = subscriptionChildren.filter((c) => canSee(c.path));
  const visibleTicketChildren = ticketChildren.filter((c) => canSee(c.path));
  const showReportsGroup = visibleSubscriptionChildren.length > 0 || visibleTicketChildren.length > 0;
  const visibleLandingPageChildren = landingPageChildren.filter((c) => canSee(c.path));
  const showLandingPageGroup = visibleLandingPageChildren.length > 0;

  // Reports group expands on click; auto-expanded when a report page is open.
  // Inside Reports, the Subscription and Regular Tickets groups each expand on click
  const [reportsOpen, setReportsOpen] = useState(
    () => allReportChildren.some((c) => location.pathname.startsWith(c.path))
  );
  const [subscriptionOpen, setSubscriptionOpen] = useState(
    () => subscriptionChildren.some((c) => location.pathname.startsWith(c.path))
  );
  const [ticketsOpen, setTicketsOpen] = useState(
    () => ticketChildren.some((c) => location.pathname.startsWith(c.path))
  );
  const [landingPageOpen, setLandingPageOpen] = useState(
    () => landingPageChildren.some((c) => location.pathname.startsWith(c.path))
  );

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const reportsActive = allReportChildren.some((c) => isActive(c.path));
  const subscriptionActive = subscriptionChildren.some((c) => isActive(c.path));
  const ticketsActive = ticketChildren.some((c) => isActive(c.path));
  const landingPageActive = landingPageChildren.some((c) => isActive(c.path));

  // Shared renderer for one report link (indented under its group)
  const renderReportLink = (item) => {
    const Icon = item.icon;
    const active = isActive(item.path);

    return (
      <li key={item.path}>
        <NavLink
          to={item.path}
          onClick={handleNavClick}
          className={`
            flex items-center gap-2.5 pl-10 pr-3 py-2 rounded-lg
            transition-all duration-200 relative text-[13px]
            ${active
              ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/25'
              : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
            }
          `}
        >
          <Icon className="w-4 h-4 flex-shrink-0" />
          <span className="font-medium leading-snug">{item.label}</span>
        </NavLink>
      </li>
    );
  };

  const handleNavClick = () => {
    if (window.innerWidth < 1024) onClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/superadmin/login');
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full w-64
          bg-gradient-to-b from-gray-900 to-gray-800
          flex flex-col
          transform transition-transform duration-300 ease-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-700">
          <div className="w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center shadow-lg">
            <span className="text-white text-xl">🔐</span>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-white text-lg">TeleValley</h1>
            <p className="text-xs text-gray-400">SuperAdmin Panel</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-700 lg:hidden transition-colors"
          >
            <HiOutlineX className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* User Info */}
        <div className="px-4 py-3">
          <div className="px-3 py-3 rounded-xl bg-gray-700/50 border border-gray-600">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-semibold text-sm">
                  {superAdmin?.name?.charAt(0) || 'S'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white text-sm truncate">
                  {superAdmin?.name || 'Super Admin'}
                </p>
                <p className="text-xs text-gray-400 truncate">
                  {superAdmin?.role || 'super_admin'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-2">
          <p className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Menu
          </p>
          <ul className="space-y-1">
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={handleNavClick}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-xl
                      transition-all duration-200 relative
                      ${active
                        ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/25'
                        : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </NavLink>
                </li>
              );
            })}

            {/* ── Reports (click to expand) — hidden entirely if no sub-report is visible ── */}
            {showReportsGroup && (
            <li>
              <button
                onClick={() => setReportsOpen((v) => !v)}
                className={`
                  flex items-center gap-3 w-full px-3 py-2.5 rounded-xl
                  transition-all duration-200 relative
                  ${reportsActive
                    ? 'bg-primary-500/20 text-white'
                    : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
                  }
                `}
              >
                <HiOutlineChartSquareBar className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm font-medium flex-1 text-left">Reports</span>
                {reportsOpen ? (
                  <HiOutlineChevronDown className="w-4 h-4 flex-shrink-0" />
                ) : (
                  <HiOutlineChevronRight className="w-4 h-4 flex-shrink-0" />
                )}
              </button>

              {/* Sub-sections — two collapsible groups */}
              {reportsOpen && (
                <ul className="mt-1 space-y-0.5">
                  {/* ── Subscription group ── */}
                  {visibleSubscriptionChildren.length > 0 && (
                  <li>
                    <button
                      onClick={() => setSubscriptionOpen((v) => !v)}
                      className={`
                        flex items-center gap-2 w-full pl-6 pr-3 py-2 rounded-lg
                        transition-all duration-200 text-[11px] font-semibold uppercase tracking-wider
                        ${subscriptionActive
                          ? 'text-primary-400'
                          : 'text-gray-500 hover:text-gray-300'
                        }
                      `}
                    >
                      <span className="flex-1 text-left">Subscription</span>
                      {subscriptionOpen ? (
                        <HiOutlineChevronDown className="w-3.5 h-3.5 flex-shrink-0" />
                      ) : (
                        <HiOutlineChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
                      )}
                    </button>
                    {subscriptionOpen && (
                      <ul className="space-y-0.5">
                        {visibleSubscriptionChildren.map(renderReportLink)}
                      </ul>
                    )}
                  </li>
                  )}

                  {/* ── Support Tickets group ── */}
                  {visibleTicketChildren.length > 0 && (
                  <li>
                    <button
                      onClick={() => setTicketsOpen((v) => !v)}
                      className={`
                        flex items-center gap-2 w-full pl-6 pr-3 py-2 rounded-lg
                        transition-all duration-200 text-[11px] font-semibold uppercase tracking-wider
                        ${ticketsActive
                          ? 'text-primary-400'
                          : 'text-gray-500 hover:text-gray-300'
                        }
                      `}
                    >
                      <span className="flex-1 text-left">Support Tickets</span>
                      {ticketsOpen ? (
                        <HiOutlineChevronDown className="w-3.5 h-3.5 flex-shrink-0" />
                      ) : (
                        <HiOutlineChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
                      )}
                    </button>
                    {ticketsOpen && (
                      <ul className="space-y-0.5">
                        {visibleTicketChildren.map(renderReportLink)}
                      </ul>
                    )}
                  </li>
                  )}
                </ul>
              )}
            </li>
            )}

            {/* ── Landing Page (click to expand) — content shown on the public site ── */}
            {showLandingPageGroup && (
            <li>
              <button
                onClick={() => setLandingPageOpen((v) => !v)}
                className={`
                  flex items-center gap-3 w-full px-3 py-2.5 rounded-xl
                  transition-all duration-200 relative
                  ${landingPageActive
                    ? 'bg-primary-500/20 text-white'
                    : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
                  }
                `}
              >
                <HiOutlinePhotograph className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm font-medium flex-1 text-left">Landing Page</span>
                {landingPageOpen ? (
                  <HiOutlineChevronDown className="w-4 h-4 flex-shrink-0" />
                ) : (
                  <HiOutlineChevronRight className="w-4 h-4 flex-shrink-0" />
                )}
              </button>

              {landingPageOpen && (
                <ul className="mt-1 space-y-0.5">
                  {visibleLandingPageChildren.map(renderReportLink)}
                </ul>
              )}
            </li>
            )}

            {visibleBottomNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={handleNavClick}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-xl
                      transition-all duration-200 relative
                      ${active
                        ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/25'
                        : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Logout */}
        <div className="p-3 border-t border-gray-700">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-gray-300 hover:bg-red-500/10 hover:text-red-400 transition-all duration-200"
          >
            <HiOutlineLogout className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export const SuperAdminMobileHeader = ({ onMenuClick }) => {
  const { superAdmin } = useSuperAdmin();

  return (
    <header className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-gray-900/95 backdrop-blur-lg border-b border-gray-700">
      <div className="flex items-center justify-between px-4 py-2.5">
        <button
          onClick={onMenuClick}
          className="p-2 -ml-2 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors"
        >
          <HiOutlineMenu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center shadow-md">
            <span className="text-white">🔐</span>
          </div>
          <span className="font-bold text-white">TeleValley</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Renewal notifications bell */}
          <RenewalNotificationBell />
          {/* Same theme toggle as the admin panel's mobile header */}
          <ThemeToggle />
          <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
            <span className="text-white font-semibold text-xs">
              {superAdmin?.name?.charAt(0) || 'S'}
            </span>
          </div>
        </div>
      </div>
    </header>
  );
};

export default SuperAdminSidebar;